import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { AuthService } from './auth.service';
import { useAuthStore } from './auth.store';
import {
  endViewAsLocally,
  readViewAs,
  saveViewAs,
  viewAsUser,
} from './view-as';

/** Where a view-as begins: a page every role can open. */
const VIEW_AS_LANDING = '/today';

type StartViewAsValues = {
  companyId: string;
  roleId: string;
  branchIds?: string[];
};

/**
 * Starts viewing the CMS as a company role. Lands on a full page load rather
 * than a navigation: the role, the menu, the permissions, the cache and every
 * screen's local state all change at once, and a fresh document is the one
 * teardown that cannot miss any of them.
 */
const useStartViewAs = () =>
  useMutation({
    mutationFn: async ({ companyId, roleId, branchIds }: StartViewAsValues) => {
      const { access_token: adminToken, user } = useAuthStore.getState();
      if (!adminToken) throw new Error('Your session has ended');

      // Switching from one view-as to the next keeps the Admin's own user.
      const previous = readViewAs();
      const adminUser = previous?.adminUser ?? user;

      const response = await AuthService.startViewAs({
        adminToken,
        companyId,
        roleId,
        branchIds: branchIds?.length ? branchIds : undefined,
      });

      const data = response?.data;
      if (!data?.access_token) throw new Error('Something went wrong');

      if (previous) {
        void AuthService.logout(previous.token).catch(() => undefined);
      }

      saveViewAs({
        token: data.access_token,
        session: data.view_as,
        adminUser,
      });
      // The landing page's permission check reads the persisted user before
      // the layout has loaded the profile.
      useAuthStore
        .getState()
        .setUser(viewAsUser(data.view_as, roleId, adminUser));
    },
    onSuccess: () => {
      window.location.assign(VIEW_AS_LANDING);
    },
    onError: (error: { message?: string } | null) => {
      toast.error(error?.message ?? 'Something went wrong');
    },
  });

/**
 * Ends the view-as and hands the CMS back to the Admin, on the page they were
 * on (an Admin can open every page a company role can). `endRemote: false`
 * skips the logout call, for a session that has already expired.
 */
const useExitViewAs = () => {
  const [isExiting, setIsExiting] = useState(false);

  const exit = useCallback(async ({ endRemote = true } = {}) => {
    const viewAs = readViewAs();
    setIsExiting(true);

    if (viewAs && endRemote) {
      await AuthService.logout(viewAs.token).catch(() => undefined);
    }

    endViewAsLocally();
    window.location.reload();
  }, []);

  return { exit, isExiting };
};

export type { StartViewAsValues };
export { useStartViewAs, useExitViewAs };
